"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type StretchingDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A calm lavender studio at dusk: a tall window glowing peach on the left, a
 * ballet barre along the wall, resistance bands on hooks and a snake plant on
 * the right, and someone folded into a seated forward fold on a lilac mat in
 * the middle (the focal point). A foam roller waits on the floor at the left,
 * clear of the bottom-centre participant pill.
 *
 * Motion: the window light breathes — one CSS opacity loop, off under reduced
 * motion.
 */
const STRETCHING_KEYFRAMES = `
@keyframes stretching-light { 0%, 100% { opacity: 0.7 } 50% { opacity: 1 } }
.stretching-light { animation: stretching-light 7s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .stretching-light { animation: none }
}
`;

/** Where the wall meets the floor, in px from the top of the scene. */
const HORIZON = 122;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `stretching-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("floor")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#c8a7b8" />
          <stop offset="1" stopColor="#6e4f7e" />
        </linearGradient>
        <linearGradient id={id("dusk")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#8e7fd6" />
          <stop offset="0.55" stopColor="#f2a3b6" />
          <stop offset="1" stopColor="#ffd1a1" />
        </linearGradient>
        <linearGradient id={id("beam")} x1="0" x2="1" y1="0" y2="0.5">
          <stop offset="0" stopColor="#ffd9c2" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffd9c2" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("mat")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e2d3f7" />
          <stop offset="1" stopColor="#b59ae0" />
        </linearGradient>
        <linearGradient id={id("roller")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#9fd6ff" />
          <stop offset="0.5" stopColor="#4aa3f0" />
          <stop offset="1" stopColor="#1f6fc4" />
        </linearGradient>
        <linearGradient id={id("barre")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#f2d2a6" />
          <stop offset="1" stopColor="#a8754a" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Wall, barre, floor and the window's light — stretches with the card. */
function Studio({ id }: { id: IdFn }) {
  const h = HORIZON;
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Skirting */}
      <rect x="0" y={h - 5} width="300" height="5" fill="#4a3366" />
      <rect
        x="0"
        y={h - 5}
        width="300"
        height="1"
        fill="#e9d8ff"
        opacity="0.4"
      />
      {/* Floor */}
      <rect
        x="0"
        y={h}
        width="300"
        height={220 - h}
        fill={`url(#${id("floor")})`}
      />
      {[30, 70, 110, 150, 190, 230, 270].map((x) => (
        <line
          key={x}
          x1={x}
          y1={h}
          x2={(x - 150) * 2.4 + 150}
          y2="220"
          stroke="#3e2852"
          strokeOpacity="0.25"
          strokeWidth="0.8"
        />
      ))}
      {/* Window light: a beam, and its patch on the floor */}
      <g className="stretching-light">
        <polygon
          points={`18,24 76,24 220,${h + 50} 110,${h + 50}`}
          fill={`url(#${id("beam")})`}
        />
        <polygon
          points={`70,${h + 4} 150,${h + 4} 214,${h + 52} 104,${h + 52}`}
          fill="#ffd7bf"
          opacity="0.2"
        />
      </g>
      {/* Barre: rail + brackets */}
      <rect
        x="0"
        y="88"
        width="300"
        height="3.4"
        rx="1.7"
        fill={`url(#${id("barre")})`}
      />
      <rect x="0" y="94" width="300" height="2" fill="#2e1d45" opacity="0.2" />
    </svg>
  );
}

function TallWindow({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-[22px] left-[5%]"
      width="58"
      height="96"
      viewBox="0 0 58 96"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="58" height="96" rx="2" fill="#f1e8ff" />
      <rect
        x="4"
        y="4"
        width="50"
        height="88"
        rx="1"
        fill={`url(#${id("dusk")})`}
      />
      <circle cx="36" cy="66" r="9" fill="#fff0d6" opacity="0.95" />
      <path
        d="M4 78 Q18 70 30 76 Q42 70 54 74 V92 H4 Z"
        fill="#7a5aa6"
        opacity="0.8"
      />
      <rect x="27.5" y="4" width="3" height="88" fill="#f1e8ff" />
      <rect x="4" y="34" width="50" height="3" fill="#f1e8ff" />
      <rect x="4" y="62" width="50" height="3" fill="#f1e8ff" />
    </svg>
  );
}

function BarreBrackets() {
  return (
    <>
      {["left-[34%]", "left-[66%]"].map((pos) => (
        <div
          key={pos}
          className={`absolute top-[88px] ${pos} h-[12px] w-[4px] rounded-b-[2px] bg-[#cfd2da] shadow-[1px_2px_2px_rgba(46,29,69,0.35)]`}
        />
      ))}
    </>
  );
}

function BandsAndPlant() {
  return (
    <svg
      className="absolute top-[30px] right-[3%]"
      width="78"
      height="100"
      viewBox="0 0 78 100"
      aria-hidden="true"
      focusable="false"
    >
      {/* Hooks + resistance bands */}
      <rect x="6" y="4" width="34" height="4" rx="2" fill="#e8e2f2" />
      {[
        { x: 12, c: "#ff7aa8", l: 34 },
        { x: 22, c: "#ffc24a", l: 28 },
        { x: 32, c: "#5ad1b6", l: 38 },
      ].map((b) => (
        <path
          key={b.x}
          d={`M${b.x} 8 Q${b.x - 5} ${8 + b.l} ${b.x} ${8 + b.l} Q${b.x + 5} ${8 + b.l} ${b.x} 8`}
          stroke={b.c}
          strokeWidth="2.6"
          fill="none"
        />
      ))}
      {/* Snake plant */}
      {[
        "M58 92 Q52 54 56 30 Q62 56 62 92 Z",
        "M62 92 Q64 46 72 22 Q72 58 66 92 Z",
        "M56 92 Q44 64 46 44 Q54 66 60 92 Z",
        "M64 92 Q72 66 76 52 Q74 74 68 92 Z",
      ].map((d) => (
        <path key={d} d={d} fill="#3f7a52" />
      ))}
      <path
        d="M56 30 Q60 56 61 90 M72 22 Q70 56 65 90"
        stroke="#c9e88a"
        strokeWidth="0.9"
        fill="none"
      />
      {/* Pot */}
      <path d="M48 86 H78 L75 100 H51 Z" fill="#f4ede4" />
      <rect x="47" y="84" width="32" height="4" rx="1.2" fill="#ffffff" />
    </svg>
  );
}

function Mat({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute left-[calc(50%-74px)]"
      style={{ top: HORIZON + 14 }}
      width="148"
      height="28"
      viewBox="0 0 148 28"
      aria-hidden="true"
      focusable="false"
    >
      <polygon
        points="18,2 130,2 148,25 0,25"
        fill="#2e1d45"
        opacity="0.25"
        transform="translate(0 2)"
      />
      <polygon points="18,0 130,0 148,24 0,24" fill={`url(#${id("mat")})`} />
      <polygon points="0,24 148,24 148,27 0,27" fill="#8b6fbf" />
      <line
        x1="18"
        y1="0.5"
        x2="130"
        y2="0.5"
        stroke="#fff"
        strokeOpacity="0.7"
      />
    </svg>
  );
}

/** Seated forward fold: legs long, chest down over them, hands to feet. */
function FoldFigure() {
  const skin = "#9a5f44";
  const top = "#f6eefc";
  const tights = "#3d2a5c";
  const rim = "#ffe1c9";
  return (
    <svg
      className="absolute left-[calc(50%-60px)]"
      style={{ top: HORIZON - 14 }}
      width="120"
      height="55"
      viewBox="0 0 96 44"
      aria-hidden="true"
      focusable="false"
    >
      {/* Legs, straight along the mat */}
      <path
        d="M72 36 L18 37"
        stroke={tights}
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
      />
      {/* Flexed feet */}
      <path
        d="M14 39 L12 30"
        stroke={skin}
        strokeWidth="3.6"
        strokeLinecap="round"
      />
      {/* Hips + folded torso */}
      <path
        d="M74 33 Q70 20 50 24"
        stroke={top}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="73" cy="34" r="6" fill={tights} />
      {/* Arms reaching to the feet */}
      <path
        d="M52 26 L16 31"
        stroke={skin}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      {/* Head tucked toward the knees + bun */}
      <circle cx="42" cy="27" r="6" fill={skin} />
      <path d="M38 22 A6 6 0 0 1 47 23.6 L42 25 Z" fill="#2a1a12" />
      <circle cx="47.5" cy="20.4" r="2.8" fill="#2a1a12" />
      {/* Window-side rim light along the back */}
      <path
        d="M78 27 Q72 14 52 17.4 M36.4 24 A6 6 0 0 1 40 21.2"
        stroke={rim}
        strokeOpacity="0.9"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function FoamRoller({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute bottom-[18px] left-[3%]"
      width="64"
      height="26"
      viewBox="0 0 64 26"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="32" cy="23" rx="30" ry="2.6" fill="#2e1d45" opacity="0.4" />
      <rect
        x="6"
        y="2"
        width="52"
        height="19"
        rx="2"
        fill={`url(#${id("roller")})`}
      />
      {[14, 22, 30, 38, 46].map((x) => (
        <rect
          key={x}
          x={x}
          y="2"
          width="1.6"
          height="19"
          fill="#0e3f7a"
          opacity="0.28"
        />
      ))}
      <ellipse cx="6" cy="11.5" rx="4" ry="9.5" fill="#2f86d8" />
      <ellipse cx="6" cy="11.5" rx="2.4" ry="6" fill="#1a5aa0" />
      <rect
        x="8"
        y="4"
        width="48"
        height="2"
        rx="1"
        fill="#fff"
        opacity="0.55"
      />
    </svg>
  );
}

export function StretchingDisplayPreview({
  participants,
}: StretchingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Stretching"
      backgroundClassName="bg-[#6e55a6]"
      participants={participants}
      scene={
        <>
          <style href="stretching-scene-keyframes" precedence="default">
            {STRETCHING_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Lavender wall, warmed from the window side */}
          <div className="absolute inset-0 bg-[radial-gradient(110%_95%_at_18%_30%,#c3a9ec_0%,#9478cf_40%,#5f4596_100%)]" />
          <Studio id={id} />
          <TallWindow id={id} />
          <BarreBrackets />
          <BandsAndPlant />

          <Mat id={id} />
          <FoldFigure />
          <FoamRoller id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_45%_35%,transparent_50%,rgba(30,16,52,0.5)_100%)]" />
      }
    />
  );
}
